<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Article, MandatoryInfoItem } from '../../types'
import { useNavigationStore } from '../../stores/navigationStore'
import DebugPanel from './DebugPanel.vue'

const props = defineProps<{
  article: Article
}>()

const navStore = useNavigationStore()
const showDebug = ref(false)

const fallbackImg = '/Themes/Cylmo/Content/images/default-image.png'

// Prefer the full criteria list; fall back to the mandatory subset when the
// backend didn't send allCriteria (old cache entries).
const specs = computed<MandatoryInfoItem[]>(() => {
  const all = props.article.allCriteria
  if (all && all.length > 0) return all
  return props.article.mandatoryCriteria ?? []
})

// Critical fitment criteria for the searched vehicle's engine (VIN-from/to, model-year-from).
// Shown prominently so the customer can self-verify — no fit/no-fit verdict, no badge.
const criticalSpecs = computed(() => specs.value.filter(c => c.isCritical))
const normalSpecs = computed(() => specs.value.filter(c => !c.isCritical))

function addToCart(productId: number, e: Event) {
  e.preventDefault()
  e.stopPropagation()
  const url = `/addproducttocart/catalog/${productId}/1/1`
  if ((window as any).AjaxCart) {
    ;(window as any).AjaxCart.addproducttocart_catalog(url)
  } else {
    window.location.href = url
  }
}

function openDebug(e: Event) {
  e.preventDefault()
  e.stopPropagation()
  showDebug.value = true
}

function navigateToProduct() {
  if (props.article.productUrl) {
    window.location.href = props.article.productUrl
  }
}
</script>

<template>
  <div
    class="part-row"
    :class="{ 'part-row--position-mismatch': article.positionMismatch, 'part-row--clickable': !!article.productUrl }"
    @click="navigateToProduct"
  >
    <!-- Image thumbnail -->
    <div class="part-row__image">
      <img
        :src="article.imageUrl || fallbackImg"
        :alt="article.genericArticleDescription"
        loading="lazy"
        @error="($event.target as HTMLImageElement).src = fallbackImg"
      />
    </div>

    <!-- Main info + specs -->
    <div class="part-row__main">
      <div class="part-row__header">
        <div class="part-row__title-group">
          <p class="part-row__brand">{{ article.mfrName }}</p>
          <p class="part-row__title">{{ article.genericArticleDescription }}</p>
          <p class="part-row__sku">Art.nr: {{ article.articleNumber }}</p>
        </div>
        <div class="part-row__badges">
          <div
            v-if="article.positionMismatch"
            class="part-row__position-warn"
            title="Denne delen kan være for motsatt posisjon (for/bak). Sjekk spesifikasjonene før kjøp."
          >
            ⚠ Posisjon ikke bekreftet
          </div>
        </div>
      </div>

      <!-- Critical fitment criteria for the searched vehicle's engine (VIN / model-year limits).
           Informational — the customer verifies against their own vehicle. -->
      <div v-if="criticalSpecs.length > 0" class="part-row__fitment-criteria">
        <span class="part-row__fitment-criteria-label">Tilpasning for ditt kjøretøy</span>
        <div class="part-row__fitment-criteria-items">
          <div v-for="(c, i) in criticalSpecs" :key="i" class="part-row__fitment-criterion">
            <span class="part-row__fc-label">{{ c.description }}</span>
            <span class="part-row__fc-value">
              {{ c.value }}<template v-if="c.unit"> {{ c.unit }}</template>
            </span>
          </div>
        </div>
      </div>

      <!-- Full criteria table -->
      <div v-if="normalSpecs.length > 0" class="part-row__specs">
        <div v-for="(c, i) in normalSpecs" :key="i" class="part-row__spec">
          <span class="part-row__spec-label">{{ c.description }}</span>
          <span class="part-row__spec-value">
            {{ c.value }}<template v-if="c.unit"> {{ c.unit }}</template>
          </span>
        </div>
      </div>
    </div>

    <!-- Price + cart action -->
    <div class="part-row__action">
      <template v-if="article.nopProductId">
        <span
          v-if="article.stockAvailability"
          class="part-row__stock"
          :class="{
            'stock--in': article.stockAvailability === 'På lager',
            'stock--order': article.stockAvailability === 'Bestillingsvare',
            'stock--out': article.stockAvailability === 'Ikke på lager',
          }"
        >
          {{ article.stockAvailability }}
        </span>
        <span v-if="article.price" class="part-row__price">{{ article.price }}</span>
        <button
          class="part-row__cart-btn"
          @click="addToCart(article.nopProductId!, $event)"
        >
          Kjøp
        </button>
      </template>
      <a
        v-else
        href="/contactus-2"
        class="part-row__contact-link"
        @click.stop
      >
        Kontakt oss
      </a>
    </div>

    <!-- Admin debug button (TecdocEditor only) -->
    <button
      v-if="navStore.canEditCategories"
      class="part-row__debug-btn"
      title="Vis rå TecDoc-data for denne delen"
      @click="openDebug"
    >
      Debug
    </button>
    <DebugPanel
      v-if="showDebug"
      :sku="article.articleNumber"
      @close="showDebug = false"
    />
  </div>
</template>

<style scoped lang="scss">
.part-row {
  position: relative;
  display: grid;
  grid-template-columns: 96px 1fr auto;
  gap: 1rem;
  padding: 0.875rem 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: border-color 0.15s, box-shadow 0.15s;

  & + & {
    margin-top: 0.5rem;
  }

  &--clickable {
    cursor: pointer;

    &:hover {
      border-color: #002868;
      box-shadow: 0 1px 6px rgba(0, 40, 104, 0.12);
    }
  }

  &--position-mismatch {
    outline: 2px solid #f59e0b;
    outline-offset: -2px;
  }

  &__image {
    width: 96px;
    height: 96px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f9fafb;
    border-radius: 6px;
    padding: 4px;
    overflow: hidden;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  &__main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
  }

  &__title-group {
    min-width: 0;
    flex: 1;
  }

  &__brand {
    font-size: 0.7rem;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    margin: 0 0 0.125rem;
  }

  &__title {
    font-weight: 600;
    font-size: 0.95rem;
    line-height: 1.25;
    margin: 0 0 0.25rem;
    color: #111827;
  }

  &__sku {
    font-size: 0.75rem;
    color: #888;
    font-family: monospace;
    margin: 0;
  }

  &__badges {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    flex-shrink: 0;
  }

  &__position-warn {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #f59e0b;
    font-size: 0.65rem;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 4px;
    letter-spacing: 0.3px;
    line-height: 1.2;
    white-space: nowrap;
  }

  &__fitment-criteria {
    margin-top: 0.25rem;
    background: #fff7ed;
    border: 1px solid #fdba74;
    border-left: 3px solid #f59e0b;
    border-radius: 6px;
    padding: 0.4rem 0.6rem;
  }

  &__fitment-criteria-label {
    display: block;
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #b45309;
    margin-bottom: 0.25rem;
  }

  &__fitment-criteria-items {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 0.15rem 1rem;
  }

  &__fitment-criterion {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 0.8rem;
    min-width: 0;
  }

  &__fc-label {
    color: #92400e;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &__fc-value {
    color: #7c2d12;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
  }

  &__specs {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.25rem 1rem;
    padding-top: 0.375rem;
    border-top: 1px dashed #e5e7eb;
  }

  &__spec {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 0.78rem;
    min-width: 0;
  }

  &__spec-label {
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
    min-width: 0;
  }

  &__spec-value {
    color: #111827;
    font-weight: 500;
    flex-shrink: 0;
    text-align: right;
  }

  &__action {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    gap: 0.375rem;
    flex-shrink: 0;
    min-width: 120px;
  }

  &__stock {
    font-size: 0.8rem;
    font-weight: 500;

    &.stock--in { color: #16a34a; }
    &.stock--order { color: #d97706; }
    &.stock--out { color: #dc2626; }
  }

  &__price {
    font-weight: 700;
    font-size: 1.05rem;
    color: #111827;
  }

  &__cart-btn {
    background: #f5a623;
    color: white;
    border: none;
    padding: 8px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.85rem;
    white-space: nowrap;

    &:hover { background: #e09000; }
  }

  &__contact-link {
    color: #002868;
    font-size: 0.8rem;
    text-decoration: underline;
    text-align: right;

    &:hover { color: #001d4f; }
  }

  &__debug-btn {
    position: absolute;
    top: 6px;
    left: 6px;
    background: rgba(31, 41, 55, 0.85);
    color: white;
    border: none;
    font-size: 0.6rem;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 4px;
    cursor: pointer;
    letter-spacing: 0.5px;
    text-transform: uppercase;

    &:hover { background: #111827; }
  }
}

@media (max-width: 768px) {
  .part-row {
    grid-template-columns: 72px 1fr;

    &__image {
      width: 72px;
      height: 72px;
    }

    &__action {
      grid-column: 1 / -1;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      min-width: 0;
      padding-top: 0.5rem;
      border-top: 1px dashed #e5e7eb;
    }

    &__specs {
      grid-template-columns: 1fr;
    }
  }
}
</style>
